import type {DragJobData, Job} from '@/shared/utils/crud-helper/models'
import {removeDragTagPreview, setDragTagPreview} from '@/shared/utils/dragTagPreview'

/**
 * Drag a Jobs-panel row onto the AI chat composer, where it becomes a "Job: <name>" chip and is sent
 * to the agent by its run id (`SessionId`, the WES run id the cancel call also uses).
 */

type JobRow = Pick<Job, 'SessionId' | 'serviceName' | 'customId'>

/** The drop payload for a job row, or `null` when the row has no run id. */
export function jobDragData(job: JobRow | undefined): DragJobData | null {
  if (!job?.SessionId) return null
  const tool = job.serviceName || undefined
  const label = tool ?? job.SessionId
  return {
    type: 'job',
    id: job.SessionId,
    name: job.customId ? `${label} (${job.customId})` : label,
    ...(tool ? {tool} : {}),
  }
}

/**
 * Make a grid row element draggable for `job`. Returns false (row left alone) when the job has no
 * run id. The listeners live on the row element, which AG Grid drops when the row goes away.
 */
export function makeJobRowDraggable(row: HTMLElement, job: JobRow | undefined): boolean {
  const data = jobDragData(job)
  if (!data) return false
  let preview: HTMLElement | null = null
  row.draggable = true
  row.addEventListener('dragstart', (e) => {
    if (!e.dataTransfer) return
    e.dataTransfer.setData('application/json', JSON.stringify(data))
    e.dataTransfer.effectAllowed = 'copy'
    removeDragTagPreview(preview)
    preview = setDragTagPreview(e, `Job: ${data.name}`, 'job')
  })
  row.addEventListener('dragend', () => {
    removeDragTagPreview(preview)
    preview = null
  })
  return true
}
